import { createFileRoute } from "@tanstack/react-router";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  EmptyBoxIllustration,
  Link as MuiLink,
  Menu,
  MenuItem,
  Typography,
  useSnackbar,
  useTranslation,
} from "@ncino/pax-ui";
import { PlusIcon, CaretDownIcon } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  EditPoolContext,
  SellWorkflowModal,
} from "@/routes/sell/-internal/CreateDraftPoolWorkflow";
import { PoolBuildMode } from "@/routes/sell/-internal/CreateDraftPoolWorkflow/steps";
import { useTheme } from "@mui/material";
import { PageContentContainer } from "@/shared/components/PageContentContainer";
import {
  GetPoolForSellEditDocument,
  GetPoolForSellEditQuery,
  GetPoolForSellEditQueryVariables,
  GetPoolsForSellPageQuery,
  MarkPoolAsSoldInput,
  PoolListingDisplayStatus,
  PoolListingStage,
  PoolListingStatus,
  Report_ElementStatus,
  Report_ElementType,
  useGetPoolsForSellPageQuery,
  useMarkPoolAsSoldMutation,
  useUpdatePoolListingMutation,
  useUpdatePoolMutation,
} from "@/services/graphql/reactQuery";
import { useFetchData, useOptimisticUpdate } from "@/services/graphql/queryUtil";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useEffectivePortfolioId } from "@/shared/hooks/useActivePortfolio";
import { createReportingElement } from "@/shared/components/elements/util";
import {
  PoolUpdateProvider,
  SellPoolActions,
  SellPoolRow,
  sellPoolsToElementData,
  useSellPoolHeaders,
} from "./-internal/sellTableUtil";
import { useTableSort } from "@/shared/hooks/useTableSort";
import { useSellRouteContext } from "./-internal/SellRouteContext";
import { OrderFormBanner } from "@/shared/components/OrderFormBanner";
import { useIroncladWorkflow } from "@/services/ironclad/useIroncladWorkflow";

export const Route = createFileRoute("/sell/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation("sell");
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  // setDrawerOpen is deliberately private to setSellWorkflowModalIsOpen below: closing has to
  // remount the workflow, and routing every caller through one setter is what keeps that true.
  const [sellWorkflowModalIsOpen, setDrawerOpen] = useState(false);
  // Closing discards the workflow's state by remounting it, so opening always starts from
  // defaults instead of relying on every close path to reset each field. Remounting on close
  // rather than on open keeps the drawer's slide-in: MUI skips the appear transition for a
  // Drawer whose first render is already open.
  const [workflowInstance, setWorkflowInstance] = useState(0);
  const setSellWorkflowModalIsOpen = (open: boolean) => {
    setDrawerOpen(open);
    if (!open) setWorkflowInstance((instance) => instance + 1);
  };

  const { status, launch } = useIroncladWorkflow();
  const showOrderFormBanner = status === "unsigned" || status === "inProgress";
  const handleSignNow = () => launch();

  const [editPoolContext, setEditPoolContext] = useState<EditPoolContext | undefined>(undefined);
  const [poolBuildMode, setPoolBuildMode] = useState<PoolBuildMode>("filter");
  const [createPoolMenuAnchor, setCreatePoolMenuAnchor] = useState<HTMLElement | null>(null);
  const [saveSnackbarOpen, setSaveSnackbarOpen] = useState(false);
  const [saveFailedSnackbarOpen, setSaveFailedSnackbarOpen] = useState(false);
  const [draftPoolCompletedSnackbarOpen, setDraftPoolCompletedSnackbarOpen] = useState(false);
  const [updatingPoolId, setUpdatingPoolId] = useState<string | null>(null);
  const [isEditPoolLoading, setIsEditPoolLoading] = useState(false);

  const { portfolioId: effectivePortfolioId } = useEffectivePortfolioId();
  const { poolQuery: data, queryLoading: isLoading } = useSellRouteContext();
  const fetchPoolForEdit = useFetchData<GetPoolForSellEditQuery, GetPoolForSellEditQueryVariables>(
    GetPoolForSellEditDocument
  );

  const pools = useMemo<SellPoolRow[]>(() => {
    if (!data?.pools || !effectivePortfolioId) return [];

    return data.pools
      .filter((pool) => pool.loanPortfolioId === effectivePortfolioId)
      .filter((pool) => pool.listing?.isActive === true)
      .map((pool) => {
        const poolBalance = pool.balance;

        return {
          id: pool.id,
          listingId: pool.listing?.id,
          poolNumber: pool.poolNumber,
          internalPoolName: pool.poolName ?? "",
          loanType: pool.poolType ?? "",
          numberOfLoans: Number(pool.loanCount),
          poolBalance,
          created: pool.createdDate,
          status: pool?.listing?.loiStatus ?? pool?.listing?.listingStage ?? "",
        };
      });
  }, [data, effectivePortfolioId]);

  const queryKey = useGetPoolsForSellPageQuery.getKey({ isActiveOnly: true });
  const optimisticUpdate = useOptimisticUpdate<GetPoolsForSellPageQuery>(queryKey);

  const { mutate: updatePoolName } = useUpdatePoolMutation({
    onMutate: async ({ id, input }) => {
      return optimisticUpdate.onMutate((oldData) => {
        if (!oldData?.pools) return oldData;

        return {
          ...oldData,
          pools: oldData.pools.map((pool) =>
            pool.id === id.toString()
              ? { ...pool, poolName: input.poolName ?? pool.poolName }
              : pool
          ),
        };
      });
    },
    onError: (error, variables, context) => {
      optimisticUpdate.onError(error, variables, context);
      enqueueSnackbar("We couldn’t rename this pool. Please try again.", { variant: "error" });
    },
    onSettled: optimisticUpdate.onSettled,
    onSuccess: () => enqueueSnackbar("Pool successfully renamed", { variant: "success" }),
  });

  const { mutate: updatePoolListing } = useUpdatePoolListingMutation({
    onMutate: async ({ poolId }) => {
      return optimisticUpdate.onMutate((oldData) => {
        if (!oldData?.pools) return oldData;

        return {
          ...oldData,
          pools: oldData.pools.filter((pool) => pool.id !== poolId),
        };
      });
    },
    onError: (error, variables, context) => {
      optimisticUpdate.onError(error, variables, context);
      enqueueSnackbar("We couldn’t delete this pool. Please try again.", { variant: "error" });
    },
    onSettled: optimisticUpdate.onSettled,
    onSuccess: () => enqueueSnackbar("Pool successfully deleted", { variant: "success" }),
  });

  const handleRenamePool = (poolId: string, newName: string) => {
    updatePoolName({
      id: parseInt(poolId, 10),
      input: {
        poolName: newName,
      },
    });
  };

  const handleDeletePool = (poolId: string, listingId: string | undefined, _poolName: string) => {
    if (!listingId) {
      console.error("Cannot delete pool: listing ID is missing");
      return;
    }

    updatePoolListing({
      poolId: poolId,
      input: {
        isActive: false,
      },
    });
  };

  const { mutateAsync: updateStatusToSold } = useMarkPoolAsSoldMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      enqueueSnackbar(`${t("sellPage.markPoolAsSoldModal.successToast")}`, { variant: "success" });
    },
    onError: () =>
      enqueueSnackbar(`${t("sellPage.markPoolAsSoldModal.failToast")}`, { variant: "error" }),
  });

  const { mutateAsync: updatePoolListingStageAsync } = useUpdatePoolListingMutation({
    onMutate: async ({ poolId, input }) => {
      return optimisticUpdate.onMutate((oldData) => {
        if (!oldData?.pools) return oldData;
        return {
          ...oldData,
          pools: oldData.pools.map((pool) =>
            pool.id === poolId && pool.listing
              ? {
                  ...pool,
                  listing: {
                    ...pool.listing,
                    listingStage: input.listingStage ?? pool.listing.listingStage,
                  },
                }
              : pool
          ),
        };
      });
    },
    onError: () => optimisticUpdate.onError,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const handleUpdatePoolStatus = (
    poolId: string,
    status: PoolListingStage | PoolListingDisplayStatus
  ) => {
    let listingStatus;
    let listingStage;

    switch (status) {
      case PoolListingStage.ReadyToPublish:
      case PoolListingStage.Draft:
        listingStage = status;
        listingStatus = PoolListingStatus.Unlisted;
        break;
      case PoolListingStage.Published:
        listingStage = status;
        listingStatus = PoolListingStatus.Available;
        break;
      case PoolListingDisplayStatus.BuyerInterested:
        listingStage = PoolListingStage.Published;
        listingStatus = PoolListingStatus.Available;
        break;
      case PoolListingDisplayStatus.UnderLoi:
        listingStage = PoolListingStage.Published;
        listingStatus = PoolListingStatus.Available;
        break;
      default:
        break;
    }
    return updatePoolListingStageAsync({
      poolId,
      input: {
        listingStage,
        listingStatus,
      },
    });
  };

  const handleEditPool = async (poolId: string) => {
    const rawPool = data?.pools.find((p) => p.id === poolId);
    const pool = pools.find((p) => p.id === poolId);
    if (!pool) return;

    setPoolBuildMode("filter");
    setIsEditPoolLoading(true);
    try {
      const editData = await fetchPoolForEdit({ id: Number(poolId) });
      if (!editData.pool) throw new Error(`Pool ${poolId} not found for edit`);

      const loanIdByLoanNumber: Record<string, string> = {};
      for (const l of editData.pool.loans) {
        if (l.loanNumber) loanIdByLoanNumber[l.loanNumber] = l.id;
      }
      const poolLoanIdByLoanId: Record<string, string> = {};
      for (const pl of editData.pool.poolLoans) {
        poolLoanIdByLoanId[pl.loanId] = pl.id;
      }
      setEditPoolContext({
        poolId: pool.id,
        listingId: pool.listingId,
        poolName: pool.internalPoolName,
        poolType: pool.loanType,
        price: rawPool?.listing?.listingPercentage ?? undefined,
        existingLoanNumbers: editData.pool.loans
          .map((l) => l.loanNumber)
          .filter((n): n is string => !!n),
        loanIdByLoanNumber,
        poolLoanIdByLoanId,
      });
      setSellWorkflowModalIsOpen(true);
    } catch {
      enqueueSnackbar("We couldn't load this pool's loans. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsEditPoolLoading(false);
    }
  };

  const handleMarkSoldPool = async (poolId: string, input: MarkPoolAsSoldInput) => {
    await updateStatusToSold({ poolId, input: input });
  };

  const openWorkflowModal = (mode: PoolBuildMode) => {
    setEditPoolContext(undefined);
    setPoolBuildMode(mode);
    setCreatePoolMenuAnchor(null);
    setSellWorkflowModalIsOpen(true);
  };

  const handleSetSellWorkflowModalIsOpen = (open: boolean) => {
    if (!open) setEditPoolContext(undefined);
    setSellWorkflowModalIsOpen(open);
  };

  const actions: SellPoolActions = {
    onRename: handleRenamePool,
    onDelete: handleDeletePool,
    onUpdateStatus: handleUpdatePoolStatus,
    onEditPool: handleEditPool,
    onMarkSoldPool: handleMarkSoldPool,
  };

  const headers = useSellPoolHeaders(actions, setUpdatingPoolId);

  const { sortedData, sortColumnIndex, sortDirection, handleSort } = useTableSort(
    pools,
    headers,
    "internalPoolName"
  );

  const elementData = sellPoolsToElementData(sortedData, headers);

  const createPoolMenuItemSx = { display: "block", whiteSpace: "normal", py: 1.5 };

  const createDraftButton = (
    <>
      <Button
        variant="contained"
        startIcon={<PlusIcon />}
        endIcon={<CaretDownIcon weight="fill" />}
        onClick={(event) => setCreatePoolMenuAnchor(event.currentTarget)}
        sx={{ px: 3, py: 1.5, mt: 1, mr: 1 }}
      >
        {t("sellPage.createPoolButtonText")}
      </Button>
      <Menu
        anchorEl={createPoolMenuAnchor}
        open={!!createPoolMenuAnchor}
        onClose={() => setCreatePoolMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 340 } } }}
      >
        <MenuItem onClick={() => openWorkflowModal("filter")} sx={createPoolMenuItemSx}>
          <Typography sx={{ fontWeight: theme.typography.fontWeightBold }}>
            {t("sellPage.createPoolMenu.quickBuild.title")}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {t("sellPage.createPoolMenu.quickBuild.description")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => openWorkflowModal("savedGroup")} sx={createPoolMenuItemSx}>
          <Typography sx={{ fontWeight: theme.typography.fontWeightBold }}>
            {t("sellPage.createPoolMenu.savedGroup.title")}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {t("sellPage.createPoolMenu.savedGroup.description")}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <>
      <Backdrop open={isEditPoolLoading} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SellWorkflowModal
        key={workflowInstance}
        {...{
          saveSnackbarOpen,
          setSaveSnackbarOpen,
          saveFailedSnackbarOpen,
          setSaveFailedSnackbarOpen,
          draftPoolCompletedSnackbarOpen,
          sellWorkflowModalIsOpen,
          setDraftPoolCompletedSnackbarOpen,
          setSellWorkflowModalIsOpen: handleSetSellWorkflowModalIsOpen,
          editPoolContext,
          poolBuildMode,
        }}
      />
      {showOrderFormBanner && (
        <Box sx={{ padding: "24px", paddingBottom: "0" }}>
          <OrderFormBanner onSignNow={handleSignNow} inProgress={status === "inProgress"} />
        </Box>
      )}
      <Snackbar
        open={saveSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSaveSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSaveSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Pool successfully created as a draft.
        </Alert>
      </Snackbar>
      <Snackbar
        open={draftPoolCompletedSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setDraftPoolCompletedSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setDraftPoolCompletedSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Pool marked as ready to publish.
        </Alert>
      </Snackbar>
      <Snackbar
        open={saveFailedSnackbarOpen}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setSaveFailedSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSaveFailedSnackbarOpen(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          An error occurred. Please try again.
        </Alert>
      </Snackbar>
      <PageContentContainer>
        {!isLoading && pools.length === 0 ? (
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: "16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              padding: "16px",
            }}
          >
            <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
              {createDraftButton}
            </Box>
            <EmptyBoxIllustration />
            <Typography
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.text.primary,
                py: 0.5,
              }}
            >
              No Pools Listed Yet
            </Typography>
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                textAlign: "center",
                fontWeight: theme.typography.fontWeightMedium,
              }}
            >
              Nothing here...yet!{" "}
              <MuiLink
                onClick={() => openWorkflowModal("filter")}
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Draft a pool to get started
              </MuiLink>
            </Typography>
          </Box>
        ) : (
          <PoolUpdateProvider updatingPoolId={updatingPoolId}>
            {createReportingElement(Report_ElementType.List, {
              props: {
                showIndex: false,
                elevated: false,
                tableHeaderVariant: "dark",
                borderedSides: true,
                tableSx: { borderRadius: 0, borderRight: "none", borderLeft: "none" },
                rowHeight: 73,
                isEmpty: !isLoading && pools.length === 0,
                elementData: {
                  ...elementData,
                  status: isLoading
                    ? Report_ElementStatus.Processing
                    : Report_ElementStatus.Completed,
                },
                sortColumnIndex: sortColumnIndex,
                sortDirection: sortDirection,
                onSortChange: handleSort,
                customHeader: (
                  <Box
                    py={2}
                    px={2}
                    sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}
                  >
                    <Typography variant="h5">{t("sellPage.tableCardTitle")}</Typography>
                    {createDraftButton}
                  </Box>
                ),
                emptyStateTitle: "No Pools Listed Yet",
                emptyStateSubtext: "Nothing here...yet!",
                emptyStateAction: (
                  <MuiLink
                    onClick={() => openWorkflowModal("filter")}
                    sx={{
                      cursor: "pointer",
                      color: theme.palette.primary.main,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Draft a pool to get started
                  </MuiLink>
                ),
              },
              key: "sell-pools-table",
            })}
          </PoolUpdateProvider>
        )}
      </PageContentContainer>
    </>
  );
}
